import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { BackgroundTask, BackgroundTaskPaged, BackgroundTaskStatus } from './export-job.model';
import { HttpClient, HttpEvent, HttpEventType } from '@angular/common/http';
import { formatFileSize, SafeJSONParse } from '@app/core/helpers';
import moment from 'moment-timezone';
import { TranslateService } from '@ngx-translate/core';
import { HelperService } from '@clearviewshared/services/helper.service';
import FileSaver from 'file-saver';
import { AppConfigurationService } from '@app/shared/services/app.configuration.service';
import { Identifiers } from '@app/shared/services/app.config.type';

@Injectable({
  providedIn: 'root',
})
export class BackgroundTaskService {
  showPanel = false;
  tempTask!: BackgroundTask | null;

  downloadProgress: Record<string, { progress: number; downloading: boolean }> = {};
  private readonly tasksSubject = new BehaviorSubject<BackgroundTaskPaged>({ Downloads: [], Total: 0 });

  tasks$ = this.tasksSubject.asObservable();

  constructor(
    private http: HttpClient,
    private translateService: TranslateService,
    private appConfigurationService: AppConfigurationService
  ) {}

  getMyTask(
    payload = {
      Status: ['Pending', 'Processing', 'Completed', 'Failed'],
      Limit: 25,
      Skip: 0,
      Loading: false,
    }
  ) {
    if (payload.Loading) {
      return this.http.post('/download-jobs', payload);
    }

    return this.http.disableLoader().post('/download-jobs', payload);
  }

  togglePanel() {
    this.showPanel = true;
  }

  get tasks(): BackgroundTaskPaged {
    return this.tasksSubject.value;
  }

  freshList(tasks: BackgroundTaskPaged): void {
    const downloads = tasks.Downloads.map((x: BackgroundTask) => {
      // const payload = SafeJSONParse(String(x.payload));
      x.CreatedAt = moment(x?.CreatedAt).format('MMM D, YYYY hh:mm:ss A');
      x.CompletedAt = x.CompletedAt ? moment(x?.CompletedAt).format('MMM D, YYYY hh:mm:ss A') : '-';
      x.FileSize = x.FileSize ? formatFileSize(x.FileSize) : '-';

      x.Program = x?.Program || 'All';
      const startDate = x?.StartDate;
      const endDate = x?.EndDate;

      const fmtDate = (d: any) => (d && moment(d).isValid() ? moment(d).format('MMM D, YYYY') : '-');
      x.DateRange = `${fmtDate(startDate)} - ${fmtDate(endDate)}`;
      return x;
    });
    this.tasksSubject.next({ ...tasks, Downloads: downloads });
  }

  add(task: BackgroundTask): void {
    this.tasksSubject.next({
      Total: this.tasksSubject.value.Total,
      Downloads: [task, ...this.tasksSubject.value.Downloads],
    });
  }

  update(id: string, changes: Partial<BackgroundTask>): void {
    const updated = this.tasksSubject.value.Downloads.map((x) => (x.JobId === id ? { ...x, ...changes } : x));

    this.tasksSubject.next({ Total: this.tasksSubject.value.Total, Downloads: updated });
  }

  remove(id: string): void {
    this.tasksSubject.next({
      Total: this.tasksSubject.value.Total,
      Downloads: this.tasksSubject.value.Downloads.filter((x) => x.JobId !== id),
    });
  }

  clearCompleted(): void {
    this.tasksSubject.next({
      Total: this.tasksSubject.value.Total,
      Downloads: this.tasksSubject.value.Downloads.filter((x) => x.Status !== 'Completed'),
    });
  }

  downloadFile(url: string): Observable<HttpEvent<Blob>> {
    return this.http.disableLoader().get(url, {
      responseType: 'blob',
      observe: 'events',
      reportProgress: true,
    });
  }

  downloadAsync(task: BackgroundTask) {
    this.downloadProgress[task.JobId] = { progress: 0, downloading: true };
    this.downloadFile(String(task.FileLink)).subscribe({
      next: (event) => {
        switch (event.type) {
          case HttpEventType.DownloadProgress:
            if (event.total) {
              this.downloadProgress[task.JobId].progress = Math.round((100 * event.loaded) / event.total);
            }
            break;

          case HttpEventType.Response:
            const blob = event.body!;

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = task.Title + `.${task?.FileExtension}`;
            a.click();

            window.URL.revokeObjectURL(url);
            setTimeout(() => {
              this.downloadProgress[task.JobId].downloading = false;
            }, 1000);
            break;
        }
      },
      error: () => {
        this.downloadProgress[task.JobId].downloading = false;
        this.downloadProgress[task.JobId].progress = 0;
      },
    });
  }

  deleteFiles(ids: Array<string>): Observable<any> {
    return this.http.post('/download-jobs/delete', { Ids: ids });
  }

  retryAsync(ids: Array<string>): Observable<any> {
    return this.http.post('/download-jobs/retry', { Ids: ids });
  }

  // tslint:disable-next-line: member-ordering
  exportExelFile(data: any, filename: string) {
    const blobValue = new Blob([data['body']], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    FileSaver.saveAs(blobValue, filename + '.xlsx');
  }

  // tslint:disable-next-line: member-ordering
  exportCSVFile(data: any, filename: string) {
    const blobValue = new Blob([data['body']], {
      type: 'text/csv',
    });
    FileSaver.saveAs(blobValue, filename + '.csv');
  }

  startExport(title: string, totalRecords: number, callback: any, reportName: string = 'default') {
    let defaultThreshhold: number = 1000;
    let donwloadManagerEnabled: boolean = false;
    (window as any).DM_FILE_NAME = title;
    (window as any).DM_RECORDS_COUNT = totalRecords;
    let config = this.appConfigurationService.getByIdentifier(Identifiers.DownloadManager);
    if (config) {
      donwloadManagerEnabled = config.Fields?.enabled === true;
      let reportsConfig =
        config.Fields?.reports?.find((report: any) => report.key === reportName) ||
        config.Fields?.reports?.find((report: any) => report.key === 'default');
      if (reportsConfig?.threshold !== undefined && reportsConfig?.threshold !== null) {
        defaultThreshhold = reportsConfig.threshold;
      }
    }

    if (totalRecords < defaultThreshhold || !donwloadManagerEnabled) {
      callback({
        'DM-Records-Count': String((window as any).DM_RECORDS_COUNT),
        'DM-File-Name': String((window as any).DM_FILE_NAME),
        'DM-Report-Name': String(reportName),
      }).subscribe((res: any) => {
        const filename = this.translateService.instant(this.translateService.instant(title));
        var headers = res.headers.get('content-disposition') || 'xlsx';

        if (headers.lastIndexOf('csv') != -1) {
          this.exportCSVFile(res, filename);
        } else if (headers.lastIndexOf('xlsx')) {
          this.exportExelFile(res, filename);
        } else {
          this.exportCSVFile(res, filename);
        }
      });
    } else {
      this.showPanel = true;
      this.tempTask = {
        JobId: new Date().getTime().toString(),
        Title: title || 'File',
        Status: BackgroundTaskStatus.Queued,
        FileName: title + ' - ' + moment(new Date()).format('MMM-D-YYYY'),
        FileSize: '-',
        Progress: 0,
        RecordsCount: totalRecords,
        callback: callback,
        ReportName: reportName,
      };
    }
  }
}
